"use client";

import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FiMonitor } from "react-icons/fi";
import { FaBan } from "react-icons/fa";
import { BiWorld } from "react-icons/bi";
import mainAPI from "../../services/main/mainAPI";
import ConfirmPopUp from "../popup/confirmPopUp";
import StatusPopUp from "../popup/statusPopUp";
import { GamePreviewModal } from "../games/shared/GamePreviewModal";
import CountAllGame from "../countAllGame";
import { formatCompact2DateTime } from "../../utils/formatDatetime";
import TablePagination, { type PageSizeOption } from "../shared/TablePagination";
import { useTranslations } from "next-intl";

type ApiLessonRow = {
    unique_id?: string;
    id?: number;
    uuid?: string;
    teacher_name?: string;
    exercise_name?: string;
    create_at?: string;
    game_type?: string;
    status?: boolean;
    source?: string;
    delete_at?: string | null;
    ban_at?: string | null;
};

type ManageGameRow = {
    uniqueId: string;
    id: number;
    uuid: string;
    exerciseName: string;
    teacherName: string;
    createdAt: string;
    gameType: string;
    status: boolean;
    banAt: string | null;
};

type ConfirmDeleteTarget = {
    id: number;
    name: string;
};

type ConfirmBanTarget = {
    id: number;
    name: string;
};

type ConfirmRestoreBanTarget = {
    id: number;
    name: string;
};

type FilterState = {
    search: string;
    gameType: string;
    status: "all" | "published" | "unpublish";
    createdAt: string;
};

const gameTypeOptions = [
    { key: "find_the_match", label: "Find the match" },
    { key: "game_show_quiz", label: "Game show Quiz" },
    { key: "flip_cards", label: "Flip cards" },
    { key: "air_plan/flying_fruits", label: "Flying Fruits" },
    { key: "flappy_bird", label: "Flappy Chicken" },
    { key: "whack_a_mole", label: "Whack a mole" },
    { key: "anagram", label: "Anagram" },
    { key: "complete_the_sentence", label: "Complete the sentence" },
    { key: "situation", label: "Situation" },
] as const;

const gameTypeLabelMap: Record<string, string> = gameTypeOptions.reduce((acc, item) => {
    acc[item.key] = item.label;
    return acc;
}, {} as Record<string, string>);

const defaultFilters: FilterState = {
    search: "",
    gameType: "all",
    status: "all",
    createdAt: "",
};

function normalizeRows(response: unknown): ManageGameRow[] {
    const list = Array.isArray(response)
        ? response
        : Array.isArray((response as { data?: unknown[] })?.data)
            ? (response as { data: unknown[] }).data
            : [];

    return list
        .map((item, index) => {
            const row = item as ApiLessonRow;
            if (!row || typeof row.id !== "number") return null;

            if (row.source === "sequence") {
                return null;
            }

            const deleted = row.delete_at;
            if (deleted !== null && deleted !== undefined && String(deleted).trim() !== "") {
                return null;
            }

            return {
                uniqueId: String(row.unique_id ?? `row_${index + 1}`),
                id: row.id,
                uuid: String(row.uuid ?? ""),
                exerciseName: String(row.exercise_name ?? "-").trim() || "-",
                teacherName: String(row.teacher_name ?? "-").trim() || "-",
                createdAt: String(row.create_at ?? "").trim(),
                gameType: String(row.game_type ?? "").trim(),
                status: Boolean(row.status),
                banAt: row.ban_at ? String(row.ban_at).trim() : null,
            };
        })
        .filter(Boolean) as ManageGameRow[];
}

function isFailedResult(response: unknown) {
    return (response as { status?: string })?.status === "failed";
}

function getGameTypeLabel(key: string) {
    return gameTypeLabelMap[key] ?? (key || "-");
}

function getAdminStatusClass(isBanned: boolean, published: boolean): string {
    const base =
        "inline-flex h-9 w-[116px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold";

    if (isBanned) {
        return `${base} bg-red-500`;
    }

    if (published) {
        return `${base} bg-[#E6F8EF]`;
    }

    return `${base} bg-[#E0E0E0]`;
}

function renderAdminStatusContent(isBanned: boolean, published: boolean, iconSize = 16) {
    if (isBanned) {
        return (
            <>
                <span className="inline-flex shrink-0 items-center justify-center">
                    <FaBan size={iconSize} color="#ffffff" />
                </span>
                <span className="text-white">แบน</span>
            </>
        );
    }

    if (published) {
        return (
            <>
                <span className="inline-flex shrink-0 items-center justify-center">
                    <BiWorld size={iconSize} color="#16A34A" />
                </span>
                <span className="text-[#16A34A]">เผยแพร่</span>
            </>
        );
    }

    return (
        <>
            <span className="inline-flex shrink-0 items-center justify-center">
                <BiWorld size={iconSize} color="#3b3b3b" />
            </span>
            <span className="text-[#3b3b3b]">ไม่เผยแพร่</span>
        </>
    );
}

export default function ManageGame() {
    const tMain = useTranslations("main");
    const [rows, setRows] = useState<ManageGameRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isBanning, setIsBanning] = useState(false);
    const [restoringBanId, setRestoringBanId] = useState<number | null>(null);
    const [filters, setFilters] = useState<FilterState>(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<PageSizeOption>(20);
    const [deleteTarget, setDeleteTarget] = useState<ConfirmDeleteTarget | null>(null);
    const [banTarget, setBanTarget] = useState<ConfirmBanTarget | null>(null);
    const [restoreBanTarget, setRestoreBanTarget] = useState<ConfirmRestoreBanTarget | null>(null);
    const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
    const [statusPopup, setStatusPopup] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);

    const loadGames = async () => {
        setIsLoading(true);
        const response = await mainAPI.getAllLessonLibraryExceptDefaultGames();

        if (!response || isFailedResult(response)) {
            setRows([]);
            setStatusPopup({
                title: "ไม่สำเร็จ",
                message: "โหลดรายการเกมไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            setIsLoading(false);
            return;
        }

        setRows(normalizeRows(response));
        setIsLoading(false);
    };

    useEffect(() => {
        void loadGames();
    }, []);

    const filteredRows = useMemo(() => {
        const keyword = filters.search.trim().toLowerCase();
        const selectedCreatedAt = filters.createdAt ? new Date(filters.createdAt).getTime() : null;

        return rows.filter((row) => {
            const matchSearch = !keyword || row.exerciseName.toLowerCase().includes(keyword);
            const matchGameType = filters.gameType === "all" || row.gameType === filters.gameType;
            const matchStatus =
                filters.status === "all"
                    ? true
                    : filters.status === "published"
                        ? row.status === true
                        : row.status === false;

            const rowTime = new Date(row.createdAt).getTime();
            const matchCreatedAt =
                selectedCreatedAt === null ||
                (Number.isFinite(rowTime) && rowTime >= selectedCreatedAt);

            return matchSearch && matchGameType && matchStatus && matchCreatedAt;
        });
    }, [rows, filters]);

    const totalCount = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters.search, filters.gameType, filters.status, filters.createdAt, pageSize]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const openPreview = (event: MouseEvent, uuid: string) => {
        event.stopPropagation();
        if (!uuid || uuid === "-") {
            return;
        }

        const previewBaseUrl = "https://thaipilagame.eef.or.th";
        const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
        const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(uuid)}?preview`;
        setPreviewIframeUrl(previewUrl);
    };

    const closePreviewIframe = () => {
        setPreviewIframeUrl(null);
    };

    const handleDeleteRequest = (row: ManageGameRow) => {
        setDeleteTarget({
            id: row.id,
            name: row.exerciseName,
        });
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        setIsDeleting(true);
        const response = await mainAPI.softDeleteGameById(String(deleteTarget.id));
        setIsDeleting(false);

        if (!response || isFailedResult(response)) {
            setStatusPopup({
                title: "ไม่สำเร็จ",
                message: "ลบเกมไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            return;
        }

        setRows((prev) => prev.filter((row) => row.id !== deleteTarget.id));
        setDeleteTarget(null);
        setStatusPopup({
            title: "สำเร็จ",
            message: "ลบเกมสำเร็จ",
            type: "success",
        });
    };

    const handleBanRequest = (row: ManageGameRow) => {
        setBanTarget({
            id: row.id,
            name: row.exerciseName,
        });
    };

    const handleBan = async () => {
        if (!banTarget) return;

        setIsBanning(true);
        const response = await mainAPI.banGameByAdmin(banTarget.id);
        setIsBanning(false);

        if (!response || isFailedResult(response)) {
            setStatusPopup({
                title: "ไม่สำเร็จ",
                message: "แบนเกมไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            return;
        }

        setRows((prev) =>
            prev.map((row) =>
                row.id === banTarget.id
                    ? {
                        ...row,
                        banAt: new Date().toISOString(),
                    }
                    : row,
            ),
        );
        setBanTarget(null);
        setStatusPopup({
            title: "สำเร็จ",
            message: "แบนเกมสำเร็จ",
            type: "success",
        });

    };

    const handleRestoreBanRequest = (row: ManageGameRow) => {
        setRestoreBanTarget({
            id: row.id,
            name: row.exerciseName,
        });
    };

    const handleRestoreBan = async () => {
        if (!restoreBanTarget || restoringBanId !== null) return;

        setRestoringBanId(restoreBanTarget.id);
        const response = await mainAPI.restoreBanGameByAdmin(restoreBanTarget.id);
        setRestoringBanId(null);

        if (!response || isFailedResult(response)) {
            setStatusPopup({
                title: "ไม่สำเร็จ",
                message: "ยกเลิกแบนเกมไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            return;
        }

        setRows((prev) =>
            prev.map((item) =>
                item.id === restoreBanTarget.id
                    ? {
                        ...item,
                        banAt: null,
                    }
                    : item,
            ),
        );
        setRestoreBanTarget(null);
        setStatusPopup({
            title: "สำเร็จ",
            message: "ยกเลิกแบนเกมสำเร็จ",
            type: "success",
        });
    };

    return (
        <div className="space-y-4">
            <CountAllGame />
            {/* <div className="rounded-md bg-white p-4 shadow-sm">*/}
            <div className="p-1">
                <p className="mb-2 text-md font-bold text-[#0092D7]">Filter by:</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-[#4E4E4E]">ค้นหา</label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(event) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    search: event.target.value,
                                }))
                            }
                            placeholder="ค้นหาชื่อเกม"
                            className="h-10 w-full rounded-md border border-[#d7d7d7] bg-white px-3 text-sm text-[#4E4E4E] outline-none"
                        />
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-[#4E4E4E]">ประเภทเกม</label>
                        <div className="relative">
                            <select
                                value={filters.gameType}
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        gameType: event.target.value,
                                    }))
                                }
                                className="h-10 w-full appearance-none rounded-md border border-[#d7d7d7] bg-white px-3 pr-10 text-sm text-[#4E4E4E] outline-none cursor-pointer"
                            >
                                <option value="all">ทั้งหมด</option>
                                {gameTypeOptions.map((option) => (
                                    <option key={option.key} value={option.key}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xl text-[#9a9a9a]" />
                        </div>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-[#4E4E4E]">สถานะ</label>
                        <div className="relative">
                            <select
                                value={filters.status}
                                onChange={(event) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        status: event.target.value as FilterState["status"],
                                    }))
                                }
                                className="h-10 w-full appearance-none rounded-md border border-[#d7d7d7] bg-white px-3 pr-10 text-sm text-[#4E4E4E] outline-none cursor-pointer"
                            >
                                <option value="all">ทั้งหมด</option>
                                <option value="published">เผยแพร่</option>
                                <option value="unpublish">ไม่เผยแพร่</option>
                            </select>
                            <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xl text-[#9a9a9a]" />
                        </div>
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-[#4E4E4E]">สร้างเมื่อ</label>
                        <input
                            type="datetime-local"
                            value={filters.createdAt}
                            onChange={(event) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    createdAt: event.target.value,
                                }))
                            }
                            className="h-10 w-full rounded-md border border-[#d7d7d7] bg-white px-3 text-sm text-[#4E4E4E] outline-none"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white pb-4">
                <div className="px-4 py-2 text-lg font-bold text-[#555555]">จัดการเกม</div>

                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                        <thead>
                            <tr className="bg-[#F8F8F8]">
                                <th className="w-12 px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">#</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">เกม</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">ผู้สร้าง</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">เวลาที่สร้าง</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">ประเภท</th>
                                <th className="px-4 py-3 text-left text-md font-semibold text-[#4e4e4e]">สถานะ</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e]"> </th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-lg text-[#9a9a9a]">
                                        กำลังโหลดข้อมูล...
                                    </td>
                                </tr>
                            ) : filteredRows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-lg text-[#9a9a9a]">
                                        ไม่พบข้อมูลเกม
                                    </td>
                                </tr>
                            ) : (
                                paginatedRows.map((row, index) => (
                                    <tr key={row.uniqueId} className={index % 2 === 0 ? "bg-[#EEEFF6]" : "bg-[#F8F8F8]"}>
                                        <td className="px-4 py-2 text-md text-[#555555]">{(currentPage - 1) * pageSize + index + 1}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{row.exerciseName}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{row.teacherName}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">
                                            {formatCompact2DateTime(row.createdAt)}
                                        </td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{getGameTypeLabel(row.gameType)}</td>
                                        <td className="px-4 py-2 text-md">
                                            <span className={getAdminStatusClass(Boolean(row.banAt), row.status)}>
                                                {renderAdminStatusContent(Boolean(row.banAt), row.status, 16)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={(event) => openPreview(event, row.uuid)}
                                                    className="inline-flex w-25 cursor-pointer items-center justify-center gap-1.5 rounded-md bg-[#0092D7] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#007bb5]"
                                                >
                                                    <FiMonitor className="text-md" />
                                                    พรีวิว
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (row.banAt) {
                                                            handleRestoreBanRequest(row);
                                                            return;
                                                        }
                                                        handleBanRequest(row);
                                                    }}
                                                    disabled={restoringBanId === row.id}
                                                    className={`inline-flex w-25 cursor-pointer items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-70 ${row.banAt
                                                            ? "bg-[#FDEF5C] text-[#2B2B2B] hover:bg-[#fce94f]"
                                                            : "bg-[#F54B4B] text-white hover:bg-[#e64545]"
                                                        }`}
                                                >
                                                    <FaBan className="text-md" />
                                                    {row.banAt ? "ยกเลิกแบน" : "แบน"}
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteRequest(row)}
                                                    className="inline-flex w-25 cursor-pointer items-center justify-center gap-1.5 rounded-md bg-[#F54B4B] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#e64545]"
                                                >
                                                    <RiDeleteBin6Line className="text-md" />
                                                    ลบ
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!isLoading && filteredRows.length > 0 ? (
                    <TablePagination
                        totalCount={totalCount}
                        totalCountLabel={tMain("totalGames", { count: totalCount })}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                    />
                ) : null}
            </div>

            {deleteTarget ? (
                <ConfirmPopUp
                    title="ยืนยันการลบ"
                    message={`ต้องการลบเกม \"${deleteTarget.name}\" ใช่หรือไม่?`}
                    onConfirm={() => {
                        void handleDelete();
                    }}
                    onCancel={() => {
                        if (!isDeleting) {
                            setDeleteTarget(null);
                        }
                    }}
                    isConfirming={isDeleting}
                />
            ) : null}

            {banTarget ? (
                <ConfirmPopUp
                    title="ยืนยันการแบน"
                    message={`ต้องการแบนเกม \"${banTarget.name}\" ใช่หรือไม่?`}
                    onConfirm={() => {
                        void handleBan();
                    }}
                    onCancel={() => {
                        if (!isBanning) {
                            setBanTarget(null);
                        }
                    }}
                    isConfirming={isBanning}
                />
            ) : null}

            {restoreBanTarget ? (
                <ConfirmPopUp
                    title="ยืนยันการยกเลิกแบน"
                    message={`ต้องการยกเลิกแบนเกม \"${restoreBanTarget.name}\" ใช่หรือไม่?`}
                    onConfirm={() => {
                        void handleRestoreBan();
                    }}
                    onCancel={() => {
                        if (restoringBanId === null) {
                            setRestoreBanTarget(null);
                        }
                    }}
                    isConfirming={restoringBanId !== null}
                />
            ) : null}

            {statusPopup ? (
                <StatusPopUp
                    title={statusPopup.title}
                    message={statusPopup.message}
                    type={statusPopup.type}
                    onClose={() => setStatusPopup(null)}
                />
            ) : null}

            {previewIframeUrl ? (
                <GamePreviewModal
                    src={previewIframeUrl}
                    iframeTitle="Game Preview"
                    onClose={closePreviewIframe}
                />
            ) : null}
        </div>
    );
}
